import ApiModel from 'models/api-model';

export class SectionReviewList extends ApiModel({
  data: {
    sectionReviews: [],
    sectionReviewsMap: {},
  },
}) {
  get sectionReviews() {
    if (this.data && this.data.sectionReviews) {
      return this.data.sectionReviews;
    }
    return null;
  }

  get sectionReviewsMap() {
    if (this.data && this.data.sectionReviewsMap) {
      return this.data.sectionReviewsMap;
    }
    return {};
  }

  processResponse({ response, isOCR }) {
    let sectionsMap = {};
    const idKey = isOCR ? 'bookmarkId' : 'sectionId';
    for (let i = 0; i < response.data.length; i++) {
      const review = response.data[i];
      if (!(review[idKey] in sectionsMap)) {
        sectionsMap[review[idKey]] = {
          [review.appUserId]: true,
        };
      } else {
        sectionsMap[review[idKey]][review.appUserId] = true;
      }
    }

    return {
      data: {
        sectionReviews: response.data,
        sectionReviewsMap: sectionsMap,
      },
    };
  }

  appendSectionReview({ review, isOCR }) {
    let sectionReviewsMapCopy = { ...this.data.sectionReviewsMap };
    const sectionReviewsCopy = [...this.sectionReviews];
    const idKey = isOCR ? 'bookmarkId' : 'sectionId';

    sectionReviewsCopy.push(review);
    sectionReviewsCopy.sort(
      (a, b) => a[idKey] - b[idKey] || b.updatedDate - a.updatedDate,
    );

    if (review[idKey] in sectionReviewsMapCopy) {
      sectionReviewsMapCopy[review[idKey]][review.appUserId] = true;
    } else {
      sectionReviewsMapCopy[review[idKey]] = {
        [review.appUserId]: true,
      };
    }

    return this.merge({
      isLoaded: true,
      isLoading: false,
      error: null,
      data: {
        sectionReviews: sectionReviewsCopy,
        sectionReviewsMap: sectionReviewsMapCopy,
      },
    });
  }

  mergeSectionsReviewList({ reviewList, isOCR }) {
    const sectionReviewsMapCopy = { ...this.data.sectionReviewsMap };
    const sectionReviewsCopy = [...this.sectionReviews];
    const idKey = isOCR ? 'bookmarkId' : 'sectionId';

    // Creating a map to track existing reviews by their unique key
    // The key is a combination of idKey and appUserId
    const reviewMap = new Map();
    sectionReviewsCopy.forEach((review, index) => {
      const key = `${review[idKey]}_${review.appUserId}`;
      reviewMap.set(key, index);
    });

    // Iterate through the new reviewList and merge with existing reviews
    // If a review with the same idKey and appUserId exists, update it
    reviewList.forEach((review) => {
      const key = `${review[idKey]}_${review.appUserId}`;
      const existingIndex = reviewMap.get(key);

      if (existingIndex !== undefined) {
        sectionReviewsCopy[existingIndex] = review;
      } else {
        sectionReviewsCopy.push(review);
      }

      if (review[idKey] in sectionReviewsMapCopy) {
        sectionReviewsMapCopy[review[idKey]][review.appUserId] = true;
      } else {
        sectionReviewsMapCopy[review[idKey]] = {
          [review.appUserId]: true,
        };
      }
    });

    sectionReviewsCopy.sort(
      (a, b) => a[idKey] - b[idKey] || b.updatedDate - a.updatedDate,
    );

    return this.merge({
      isLoaded: true,
      isLoading: false,
      error: null,
      data: {
        sectionReviews: sectionReviewsCopy,
        sectionReviewsMap: sectionReviewsMapCopy,
      },
    });
  }

  removeBulkSectionReview({ reviewList, isOCR }) {
    let sectionReviewsMapCopy = { ...this.data.sectionReviewsMap };
    const idKey = isOCR ? 'bookmarkId' : 'sectionId';
    reviewList.forEach((item) => {
      if (
        item[idKey] in sectionReviewsMapCopy &&
        item?.appUserId in sectionReviewsMapCopy[item[idKey]]
      ) {
        delete sectionReviewsMapCopy[item[idKey]];
      }
    });

    const reviewListMap = new Map();
    reviewList.forEach((item) => {
      const key = `${item[idKey]}_${item.appUserId}`;
      reviewListMap.set(key, item);
    });

    const sectionReviewedCopy = this.sectionReviews.filter((entry) => {
      const key = `${entry[idKey]}_${entry.appUserId}`;
      return !reviewListMap.has(key);
    });

    return this.merge({
      isLoaded: true,
      isLoading: false,
      error: null,
      data: {
        sectionReviews: sectionReviewedCopy,
        sectionReviewsMap: sectionReviewsMapCopy,
      },
    });
  }

  removeSectionReview(payload) {
    const { sectionId, userId, isOCR } = payload;
    let sectionReviewsMapCopy = { ...this.data.sectionReviewsMap };
    if (
      sectionId in sectionReviewsMapCopy &&
      userId in sectionReviewsMapCopy[sectionId]
    ) {
      delete sectionReviewsMapCopy[sectionId][userId];
      if (Object.values(sectionReviewsMapCopy[sectionId]).length === 0) {
        delete sectionReviewsMapCopy[sectionId];
      }
    }

    return this.merge({
      isLoaded: true,
      isLoading: false,
      error: null,
      data: {
        sectionReviews: this.sectionReviews.filter(
          (entry) =>
            !(
              entry.appUserId === userId &&
              entry[isOCR ? 'bookmarkId' : 'sectionId'] === sectionId
            ),
        ),
        sectionReviewsMap: sectionReviewsMapCopy,
      },
    });
  }

  checkIfReviewAlreadyExist(currentVal, accumulator) {
    return accumulator.some((item) => {
      return (
        item.appUserId === currentVal.appUserId &&
        item.sectionId === currentVal.sectionId
      );
    });
  }

  hasSectionBeenReviewed(sectionId, isOCR) {
    return this.sectionReviews.some((review) => {
      if (isOCR) {
        return review.bookmarkId === sectionId;
      }
      return review.sectionId === sectionId;
    });
  }

  hasSectionBeenReviewedByOtherUser(currUserId, sectionId, isOCR) {
    return this.sectionReviews.some((review) => {
      if (isOCR) {
        return (
          review.bookmarkId === sectionId && review.appUserId !== currUserId
        );
      }
      return review.sectionId === sectionId && review.appUserId !== currUserId;
    });
  }

  userAlreadyReviewed(currUserId, sectionId, isOCR) {
    return this.sectionReviews.some((review) => {
      if (isOCR) {
        return (
          review.appUserId === currUserId && review.bookmarkId === sectionId
        );
      }
      return review.appUserId === currUserId && review.sectionId === sectionId;
    });
  }
}
